import { useEffect, useState, useCallback, useRef } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';
import { Button, Container, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { AppDispatch, useAppDispatch } from 'redux/store';

import { useReactFlow } from 'reactflow';
import NodeEditorLink from 'components/common/NodeEditorLink';
import { canOpenUnblockNodeInstanceDialog } from 'helpers/nodeInstanceStatus';

import { fetchFlatNodeInstance } from '../../../../../redux/slices/instances/flatNodeInstancesSlice';

import Flow from './components/editors/Flow';
import ReadOnlyProperties from './components/ui/ReadOnlyProperties';
import DefinitionVersion from './components/ui/DefinitionVersion';
import NodeInstanceNotes from './components/ui/NodeInstanceNotes';
import UnblockNodeInstanceDialog from 'components/common/UnblockNodeInstanceDialog';

import StatusHistory from './components/ui/StatusHistory';
import WaitEvents from './components/ui/WaitEvents';
import dagre from 'dagre';

// Sort flow children by edge order using dagre
const sortByEdgeOrder = (nodes: any[], edges: any[]) => {
  if (!edges?.length || !nodes?.length) return nodes;

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR' });

  nodes.forEach((n) => g.setNode(String(n.node_instance_id), { width: 100, height: 50 }));
  edges.forEach((e) => g.setEdge(String(e.source_node_instance_id), String(e.target_node_instance_id)));

  dagre.layout(g);

  return [...nodes].sort((a, b) => g.node(String(a.node_instance_id)).x - g.node(String(b.node_instance_id)).x);
};

// Map instance children into the format Flow/ReactFlow expects
const mapInstanceChildNodes = (childNodes: any[], childEdges: any[], parentNodeId: number, isFlowRoot: boolean) => {
  const sortedChildNodes = isFlowRoot && childEdges?.length > 0 ? sortByEdgeOrder(childNodes, childEdges) : childNodes;

  const mappedNodes = sortedChildNodes.map((node) => {
    const {
      node_instance_id,
      label,
      handles,
      parent_node_instance_id = null,
      flow_parent_instance_id = null,
      status,
      description,
      activated_handles,
      submit_and_forget,
    } = node;

    const hasChildren = isFlowRoot
      ? sortedChildNodes.some((n) => n.flow_parent_instance_id === node_instance_id)
      : sortedChildNodes.some((n) => n.parent_node_instance_id === node_instance_id);
    const parentInstanceId = isFlowRoot ? flow_parent_instance_id : parent_node_instance_id;
    const hasParent = sortedChildNodes.some((n) => n.node_instance_id === parentInstanceId);

    return {
      ...node,
      id: `${node_instance_id}`,
      node_instance_id,
      data: {
        label: label || node.name,
        handles,
        description,
        activated_handles,
        hasChildren,
        status,
        sub_status: node.sub_status,
        submit_and_forget: submit_and_forget === true,
        draggable: false,
      },
      ...(!!hasParent && { parentId: `${parentInstanceId}` }),
      draggable: false,
      hidden: parentInstanceId !== parentNodeId,
    };
  });

  const mappedEdges = childEdges.map((edge) => ({
    ...edge,
    id: `${edge.source_node_instance_id}-${edge.target_node_instance_id}`,
    sourceHandle: edge.source_handle,
    sourceTarget: edge.source_target,
    source: `${edge.source_node_instance_id}`,
    target: `${edge.target_node_instance_id}`,
    type: edge.type || 'bezier',
  }));

  return { child_nodes: mappedNodes, child_edges: mappedEdges };
};

// Simple dagre layout for subflow nodes
const layoutNodes = (nodes: any[], edges: any[], direction = 'LR') => {
  if (!nodes.length) return { nodes, edges };

  const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  const isHorizontal = direction === 'LR';
  const totalNodes = nodes.length;
  const nodesep = Math.min(200, Math.max(80, 100 + totalNodes * 2));
  const ranksep = Math.min(250, Math.max(100, 120 + totalNodes * 3));

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep,
    ranksep,
    edgesep: 40,
    marginx: 50,
    marginy: 50,
  });

  nodes.forEach((node) => {
    const label = node.data?.label || '';
    const handles = node.data?.handles || [];
    const width = Math.max(150, label.length * 8 + 30 + (handles.length > 0 ? 85 : 0) + 20);
    const height = Math.max(55, 28 + handles.length * 27);
    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const pos = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? 'left' : 'top',
      sourcePosition: isHorizontal ? 'right' : 'bottom',
      position: {
        x: pos.x - (pos.width || 150) / 2,
        y: pos.y - (pos.height || 55) / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Saved state for a single expanded node
interface ExpandedNodeState {
  originalNode: any;
  childNodeIds: Set<string>;
  childEdgeIds: Set<string>;
  ancestorOriginalStyles: Record<string, any>;
}

const estimateNodeDimensions = (node) => {
  const label = node.data?.label || node.label || '';
  const description = node.data?.description || node.description || '';
  const handles = node.data?.handles || node.handles || [];
  const labelWidth = label.length * 8 + 30;
  const descWidth = description ? description.length * 6 + 10 : 0;
  const handlesWidth = handles.length > 0 ? 85 : 0;
  const width = Math.max(150, Math.max(labelWidth, descWidth) + handlesWidth + 20);
  const height = Math.max(55, 28 + handles.length * 27);
  return { width, height };
};

const INST_HEADER = 35;

const getDagrePositions = (nodes: any[], edges: any[]): Record<string, { x: number; y: number }> => {
  const rootNodes = nodes.filter((n) => !n.parentNode && !n.hidden);
  if (!rootNodes.length) return {};
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: 100,
    ranksep: 200,
    marginx: 50,
    marginy: 50,
  });
  rootNodes.forEach((n) => {
    const { width: ew, height: eh } = estimateNodeDimensions(n);
    g.setNode(n.id, {
      width: n.style?.width ?? ew,
      height: n.style?.height ?? eh,
    });
  });
  const rootIds = new Set(rootNodes.map((n) => n.id));
  edges.forEach((e) => {
    const src = String(e.source || '');
    const tgt = String(e.target || '');
    if (rootIds.has(src) && rootIds.has(tgt)) {
      try {
        g.setEdge(src, tgt);
      } catch {
        // dagre throws if a node is missing; skip this edge
      }
    }
  });
  dagre.layout(g);
  const result: Record<string, { x: number; y: number }> = {};
  rootNodes.forEach((n) => {
    const pos = g.node(n.id);
    if (pos) result[n.id] = { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 };
  });
  return result;
};

const INST_GROUP_BUFFER = 60;

const getGroupChildLayout = (
  nodes: any[],
  edges: any[],
  groupId: string
): {
  positions: Record<string, { x: number; y: number }>;
  width: number;
  height: number;
} => {
  const children = nodes.filter((n) => n.parentNode === groupId && !n.hidden);
  if (!children.length) return { positions: {}, width: 0, height: 0 };

  const allPositions: Record<string, { x: number; y: number }> = {};
  const sizedNodes = nodes.map((n) => {
    if (n.type !== 'subflowGroup' || n.parentNode !== groupId) return n;
    const nested = getGroupChildLayout(nodes, edges, n.id);
    Object.assign(allPositions, nested.positions);
    if (nested.width > 0)
      return {
        ...n,
        style: { ...n.style, width: nested.width, height: nested.height },
      };
    return n;
  });

  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: 'LR',
    nodesep: 80,
    ranksep: 120,
    marginx: INST_GROUP_BUFFER,
    marginy: INST_GROUP_BUFFER,
  });
  children.forEach((n) => {
    const sized = sizedNodes.find((s) => s.id === n.id) ?? n;
    const { width: ew, height: eh } = estimateNodeDimensions(sized);
    g.setNode(n.id, {
      width: sized.style?.width ?? ew,
      height: sized.style?.height ?? eh,
    });
  });
  const childIds = new Set(children.map((n) => n.id));
  edges.forEach((e) => {
    const src = String(e.source || '');
    const tgt = String(e.target || '');
    if (childIds.has(src) && childIds.has(tgt)) {
      try {
        g.setEdge(src, tgt);
      } catch {
        // dagre throws if a node is missing; skip this edge
      }
    }
  });
  dagre.layout(g);
  let maxX = 0;
  let maxY = 0;
  children.forEach((n) => {
    const pos = g.node(n.id);
    if (!pos) return;
    const x = pos.x - pos.width / 2;
    const y = pos.y - pos.height / 2 + INST_HEADER;
    allPositions[n.id] = { x, y };
    maxX = Math.max(maxX, x + pos.width);
    maxY = Math.max(maxY, y + pos.height);
  });
  return {
    positions: allPositions,
    width: maxX + INST_GROUP_BUFFER,
    height: maxY + INST_GROUP_BUFFER,
  };
};

const InstanceViewer = ({ node_instance_id = null }) => {
  const params = useParams<{
    nodeInstanceId?: string;
    instanceName?: string;
  }>();

  // Prefer an explicit prop (the tree navigator drives the active id this way)
  // over the route param, falling back to the param for a direct route.
  const nodeInstanceId = node_instance_id ?? params.nodeInstanceId;
  const instanceName = params.instanceName ? decodeURIComponent(params.instanceName) : '';

  const dispatch = useAppDispatch();
  const { fitView } = useReactFlow();

  // Persist selected tab in URL
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'general';

  const handleTabSelect = (key: string | null) => {
    if (!key) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      { replace: true }
    );
    if (key === 'flow') {
      setTimeout(() => fitView({ padding: 0.2 }), 100);
    }
  };

  useEffect(() => {
    const prev = document.title;
    const title = nodeInstanceId ? `View Instance — ${nodeInstanceId} - ${instanceName}` : 'View Instance';

    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [nodeInstanceId, instanceName]);

  const [instance, setInstance] = useState({
    name: '',
    type: '',
    status: '',
    sub_status: '',
    run_now: false,
    parameters: [],
    scheduled_start_datetime: '',
    scheduled_start_timezone: '',
    status_updates: [],
    child_nodes: [],
    child_edges: [],
    selectedNode: null,
    definition_id: null as number | string | null,
    node_definition_id: null as number | string | null,
    node_instance_id: null as number | string | null,
  });
  const [instanceLoading, setInstanceLoading] = useState(false);
  const [instanceError, setInstanceError] = useState<string | null>(null);
  const [navSlot, setNavSlot] = useState<React.ReactNode>(null);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);
  const [instanceRefreshToken, setInstanceRefreshToken] = useState(0);
  const nodeDetailsCacheRef = useRef<Map<number, any>>(new Map());
  const rootFetchInFlightRef = useRef<number | null>(null);
  const nodeDetailsFetchInFlightRef = useRef<Set<number>>(new Set());

  // Track expanded subflow nodes for collapse/restore
  const expandedNodesRef = useRef<Map<string, ExpandedNodeState>>(new Map());

  const rootFlatChildNodesRef = useRef<any[]>([]);
  const rootFlatChildEdgesRef = useRef<any[]>([]);

  const instanceChildNodesRef = useRef<any[]>([]);
  useEffect(() => {
    instanceChildNodesRef.current = instance.child_nodes;
  }, [instance.child_nodes]);

  const { name } = instance;
  const displayName = name || instanceName || 'Unknown';
  const nodeDefinitionId = instance.definition_id ?? instance.node_definition_id ?? null;
  const nodeEditorName = name || instanceName || null;

  const setInitialNodeInstance = (response) => {
    const { payload } = response;
    if (!payload?.node_instance) return;
    const { node_instance, child_edges, child_nodes } = payload;
    const { node_instance_id: parentNodeId } = node_instance;
    const isFlowRoot = node_instance.type === 'flow';

    // Sort child nodes by edge order for flow parents
    const sortedChildNodes = node_instance.type === 'flow' && child_edges?.length > 0 ? sortByEdgeOrder(child_nodes, child_edges) : child_nodes;

    // Map nodes from API
    const mappedNodes = sortedChildNodes.map((node) => {
      const {
        node_id,
        node_instance_id,
        label,
        handles,
        parent_node_instance_id = null,
        flow_parent_instance_id = null,
        status,
        description,
        activated_handles,
        submit_and_forget,
      } = node;

      const hasChildren = node.type === 'flow';
      const parentInstanceId = isFlowRoot ? flow_parent_instance_id : parent_node_instance_id;
      const hasParent = sortedChildNodes.some((n) => n.node_instance_id === parentInstanceId);

      const draggable = false;

      return {
        ...node,
        id: `${node_instance_id}`,
        data: {
          label: label || node.node_definition_name || node.name,
          handles,
          description,
          activated_handles,
          hasChildren,
          status,
          sub_status: node.sub_status,
          submit_and_forget: submit_and_forget === true,
          draggable,
        },
        ...(!!hasParent && { parentId: `${parentInstanceId}` }),
        draggable,
        hidden: parentInstanceId !== parentNodeId,
      };
    });

    const mappedEdges = child_edges.map((edge) => ({
      ...edge,
      id: `${edge.source_node_instance_id}-${edge.target_node_instance_id}`,
      sourceHandle: edge.source_handle,
      sourceTarget: edge.source_target,
      source: `${edge.source_node_instance_id}`,
      target: `${edge.target_node_instance_id}`,
      type: edge.type || 'bezier',
    }));

    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(mappedNodes, mappedEdges);

    setInstance({
      ...node_instance,
      child_nodes: layoutedNodes,
      child_edges: layoutedEdges,
    });

    nodeDetailsCacheRef.current.set(Number(node_instance.node_instance_id), node_instance);

    // Cache the full flat list for use when expanding nested flow nodes
    rootFlatChildNodesRef.current = child_nodes || [];
    rootFlatChildEdgesRef.current = child_edges || [];
    expandedNodesRef.current.clear();

    setInstanceError(null);
  };

  useEffect(() => {
    if (!nodeInstanceId) return;
    const parsedNodeInstanceId = nodeInstanceId;
    if (rootFetchInFlightRef.current === parsedNodeInstanceId) return;

    setInstanceLoading(true);
    setInstanceError(null);
    rootFetchInFlightRef.current = parsedNodeInstanceId;

    const fetchRootInstance = async () => {
      try {
        const payload = await dispatch(fetchFlatNodeInstance(parsedNodeInstanceId)).unwrap();
        setInitialNodeInstance({ payload });
      } catch (error) {
        console.error(error);
        setInstanceError('Failed to load instance.');
      } finally {
        if (rootFetchInFlightRef.current === parsedNodeInstanceId) {
          rootFetchInFlightRef.current = null;
        }
        setInstanceLoading(false);
      }
    };

    fetchRootInstance();
  }, [dispatch, nodeInstanceId, instanceRefreshToken]);

  useEffect(() => {
    nodeDetailsCacheRef.current.clear();
  }, [nodeInstanceId]);

  const getLayoutedElements = (nodes, edges, direction = 'LR') => {
    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
    const isHorizontal = direction === 'LR';

    const totalNodes = nodes.length;

    const nodesep = Math.min(200, Math.max(80, 100 + totalNodes * 2));
    const ranksep = Math.min(250, Math.max(100, 120 + totalNodes * 3));
    const edgesep = 40;
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep,
      ranksep,
      edgesep,
      marginx: 50,
      marginy: 50,
    });

    const nodeDimensions = new Map();
    nodes.forEach((node) => {
      const dims = estimateNodeDimensions(node);
      nodeDimensions.set(node.id, dims);
      dagreGraph.setNode(node.id, { width: dims.width, height: dims.height });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source_node_instance_id, edge.target_node_instance_id);
    });

    dagre.layout(dagreGraph);
    const newNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.node_instance_id);
      const dims = nodeDimensions.get(node.id);

      const newNode = {
        ...node,
        targetPosition: isHorizontal ? 'left' : 'top',
        sourcePosition: isHorizontal ? 'right' : 'bottom',

        position: {
          x: nodeWithPosition.x - dims.width / 2,
          y: nodeWithPosition.y - dims.height / 2,
        },
      };

      return newNode;
    });

    return { nodes: newNodes, edges };
  };

  // const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
  //   nodeInstance.child_nodes,
  //   nodeInstance.child_edges
  // );

  // const showChildNodes = (parentId) => {
  //   const mappedNodes = instance.child_nodes.map((node) => {
  //     const { hidden, parent_node_instance_id } = node;

  //     if (hidden && parentId === `${parent_node_instance_id}`) {
  //       node.hidden = false;
  //     }

  //     return node;
  //   });

  //   return setInstance({ ...instance, child_nodes: mappedNodes });
  // };

  // const newInstance = {
  //   ...nodeInstance,
  //   child_nodes: layoutedNodes,
  //   child_edges: layoutedEdges,
  // };

  // const onNodeDoubleClickHandler = (e) => {
  //   const currentNodeId = e.target.getAttribute('data-id');
  //   console.log('currentNodeId', currentNodeId);
  //   // showChildNodes(currentNodeId);
  // };

  const onNodeDoubleClickHandler = useCallback(
    async (_e, node) => {
      const currentNodeId = node?.id;
      if (!currentNodeId) return;
      const hasChildren = !!node?.data?.hasChildren;
      const cachedNode = nodeDetailsCacheRef.current.get(currentNodeId);

      const showNodeDetails = (nodeDetails: any) => {
        setInstance((prev) => ({
          ...prev,
          selectedNode: { ...nodeDetails, id: currentNodeId, hasChildren },
        }));

        const timeout = window.setTimeout(() => {
          fitView();
          window.clearTimeout(timeout);
        }, 200);
      };

      if (cachedNode) {
        showNodeDetails(cachedNode);
        return;
      }
      if (nodeDetailsFetchInFlightRef.current.has(currentNodeId)) return;
      nodeDetailsFetchInFlightRef.current.add(currentNodeId);

      try {
        const payload = await dispatch(fetchFlatNodeInstance(currentNodeId)).unwrap();
        const currentNodeDetails = payload?.node_instance;
        if (!currentNodeDetails) return;

        nodeDetailsCacheRef.current.set(currentNodeId, currentNodeDetails);
        showNodeDetails(currentNodeDetails);
      } catch (error) {
        console.error('Error fetching node instance details:', error);
      } finally {
        nodeDetailsFetchInFlightRef.current.delete(currentNodeId);
      }
    },
    [dispatch, fitView]
  );

  // Subflow fetcher for instance mode
  const handleFetchSubflow = useCallback(
    async (nodeInstanceId: number) => {
      const payload = await dispatch(fetchFlatNodeInstance(nodeInstanceId)).unwrap();

      if (!payload?.node_instance) return null;

      const { node_instance, child_nodes, child_edges } = payload;
      const isFlowRoot = node_instance.type === 'flow';

      const mapped = mapInstanceChildNodes(child_nodes || [], child_edges || [], node_instance.node_instance_id, isFlowRoot);

      const { nodes: layoutedNodes, edges: layoutedEdges } = layoutNodes(mapped.child_nodes, mapped.child_edges);

      return {
        child_nodes: layoutedNodes,
        child_edges: layoutedEdges,
      };
    },
    [dispatch]
  );

  // Expand and collapse subflow inline
  const handleExpandNode = useCallback(
    async (nodeId: string) => {
      // Collapse
      const saved = expandedNodesRef.current.get(nodeId);
      if (saved) {
        const toCleanUp: string[] = [];
        expandedNodesRef.current.forEach((_, key) => {
          if (key !== nodeId && saved.childNodeIds.has(key)) {
            toCleanUp.push(key);
          }
        });
        toCleanUp.forEach((key) => expandedNodesRef.current.delete(key));

        setInstance((prev) => {
          const isDescendant = (n: any): boolean => {
            if (!n.parentNode) return false;
            if (n.parentNode === nodeId) return true;
            const parent = prev.child_nodes.find((p) => p.id === n.parentNode);
            return parent ? isDescendant(parent) : false;
          };

          let newNodes = prev.child_nodes.filter((n) => n.id !== nodeId && !isDescendant(n)).concat(saved.originalNode);

          if (saved.ancestorOriginalStyles) {
            newNodes = newNodes.map((n) => {
              if (Object.prototype.hasOwnProperty.call(saved.ancestorOriginalStyles, n.id)) {
                return { ...n, style: saved.ancestorOriginalStyles[n.id] };
              }
              return n;
            });
          }

          const allEdgeIds = new Set(saved.childEdgeIds);
          toCleanUp.forEach((key) => {
            prev.child_edges.forEach((e) => {
              if (e.id.startsWith(`subflow-${key}-`)) allEdgeIds.add(e.id);
            });
          });

          const newEdges = prev.child_edges.filter((e) => !allEdgeIds.has(e.id));

          const groupChildPositions: Record<string, { x: number; y: number }> = {};
          const groupSizeOverrides: Record<string, { width: number; height: number }> = {};
          newNodes
            .filter((n) => n.type === 'subflowGroup')
            .forEach((g) => {
              const layout = getGroupChildLayout(newNodes, newEdges, g.id);
              Object.assign(groupChildPositions, layout.positions);
              if (layout.width > 0)
                groupSizeOverrides[g.id] = {
                  width: layout.width,
                  height: layout.height,
                };
            });
          const resizedNodes = newNodes.map((n) => {
            if (groupSizeOverrides[n.id])
              return {
                ...n,
                style: { ...n.style, ...groupSizeOverrides[n.id] },
              };
            return n;
          });
          const dagrePositions = getDagrePositions(resizedNodes, newEdges);
          const layoutedNodes = resizedNodes.map((n) => {
            if (groupChildPositions[n.id]) return { ...n, position: groupChildPositions[n.id] };
            if (dagrePositions[n.id]) return { ...n, position: dagrePositions[n.id] };
            return n;
          });

          return { ...prev, child_nodes: layoutedNodes, child_edges: newEdges };
        });

        expandedNodesRef.current.delete(nodeId);
        setTimeout(() => fitView({ padding: 0.2 }), 200);
        return;
      }

      // Expand
      try {
        let allChildNodes: any[] = [];
        let allChildEdges: any[] = [];
        const directPayload = await dispatch(fetchFlatNodeInstance(Number(nodeId))).unwrap();

        const hasOurChildren = (nodes: any[]) => nodes?.some((n: any) => String(n.flow_parent_instance_id) === nodeId);

        if (hasOurChildren(directPayload?.child_nodes)) {
          allChildNodes = directPayload.child_nodes;
          allChildEdges = directPayload.child_edges || [];
        } else if (hasOurChildren(rootFlatChildNodesRef.current)) {
          allChildNodes = rootFlatChildNodesRef.current;
          allChildEdges = rootFlatChildEdgesRef.current;
        } else {
          return;
        }
        const payload = {
          child_nodes: allChildNodes,
          child_edges: allChildEdges,
        };

        const parentNodeId = nodeId;
        const directChildren = payload.child_nodes.filter((n) => String(n.flow_parent_instance_id) === parentNodeId);

        if (!directChildren.length) return;

        const directChildIds = new Set(directChildren.map((n) => String(n.node_instance_id)));
        const directEdges = payload.child_edges.filter(
          (e) => directChildIds.has(String(e.source_node_instance_id)) && directChildIds.has(String(e.target_node_instance_id))
        );

        const mappedChildren = directChildren.map((node) => ({
          ...node,
          id: `${node.node_instance_id}`,
          node_instance_id: node.node_instance_id,
          type: node.type,
          data: {
            label: node.label || node.node_definition_name || node.name,
            handles: node.handles || [],
            description: node.description,
            activated_handles: node.activated_handles || [],
            hasChildren: node.type === 'flow',
            status: node.status,
            sub_status: node.sub_status,
            submit_and_forget: node.submit_and_forget === true,
            draggable: false,
          },
          draggable: false,
        }));

        // Map edges
        const mappedEdges = directEdges.map((edge) => ({
          ...edge,
          id: `subflow-${parentNodeId}-${edge.source_node_instance_id}-${edge.target_node_instance_id}`,
          source: `${edge.source_node_instance_id}`,
          target: `${edge.target_node_instance_id}`,
          sourceHandle: edge.source_handle,
          targetHandle: edge.target_handle,
          type: edge.type || 'default',
        }));

        // Dagre layout for children
        const childDagre = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
        childDagre.setGraph({
          rankdir: 'LR',
          nodesep: 80,
          ranksep: 120,
          marginx: 40,
          marginy: 50,
        });
        mappedChildren.forEach((node) => {
          const dims = estimateNodeDimensions(node);
          childDagre.setNode(node.id, {
            width: dims.width,
            height: dims.height,
          });
        });
        mappedEdges.forEach((edge) => {
          childDagre.setEdge(edge.source, edge.target);
        });

        dagre.layout(childDagre);

        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;

        mappedChildren.forEach((node) => {
          const pos = childDagre.node(node.id);
          const w = pos.width || 150;
          const h = pos.height || 55;
          minX = Math.min(minX, pos.x - w / 2);
          minY = Math.min(minY, pos.y - h / 2);
          maxX = Math.max(maxX, pos.x + w / 2);
          maxY = Math.max(maxY, pos.y + h / 2);
        });

        const PADDING = 80;
        const HEADER = 35;
        const groupWidth = maxX - minX + PADDING * 2;
        const groupHeight = maxY - minY + PADDING * 2 + HEADER;

        // Set relative positions
        const childrenWithPositions = mappedChildren.map((node) => {
          const pos = childDagre.node(node.id);
          const w = pos.width || 150;
          const h = pos.height || 55;
          return {
            ...node,
            parentNode: parentNodeId,
            extent: 'parent' as const,
            zIndex: 10,
            position: {
              x: pos.x - w / 2 - minX + PADDING,
              y: pos.y - h / 2 - minY + PADDING + HEADER,
            },
            targetPosition: 'left',
            sourcePosition: 'right',
          };
        });

        // Save original node state for restore on collapse
        const originalNode =
          instanceChildNodesRef.current.find((n) => n.id === nodeId && !n.hidden) ?? instanceChildNodesRef.current.find((n) => n.id === nodeId);
        if (!originalNode) return;

        const childNodeIds = new Set(childrenWithPositions.map((n) => n.id));
        const childEdgeIds = new Set(mappedEdges.map((e) => e.id));

        const ANCESTOR_BUFFER = 60;
        const refNodes = instanceChildNodesRef.current.filter((n) => !n.hidden);

        const ancestorOriginalStyles: Record<string, any> = {};
        const ancestorResizes: Record<string, { width: number; height: number }> = {};
        {
          let currentId = nodeId;
          let currentW = groupWidth;
          let currentH = groupHeight;

          while (true) {
            const node = refNodes.find((n) => n.id === currentId);
            const parentId = node?.parentNode ?? null;
            if (!parentId) break;
            const parent = refNodes.find((n) => n.id === parentId);
            if (!parent) break;
            if (!ancestorOriginalStyles[parentId]) {
              ancestorOriginalStyles[parentId] = { ...(parent.style || {}) };
            }
            let childMaxX = 0;
            let childMaxY = 0;
            refNodes.forEach((n) => {
              if ((n.parentNode ?? null) !== parentId) return;
              const cx = n.position?.x ?? 0;
              const cy = n.position?.y ?? 0;
              const { width: ew, height: eh } = estimateNodeDimensions(n);
              const w = n.id === currentId ? currentW : (n.style?.width ?? ew);
              const h = n.id === currentId ? currentH : (n.style?.height ?? eh);
              childMaxX = Math.max(childMaxX, cx + w);
              childMaxY = Math.max(childMaxY, cy + h);
            });
            ancestorResizes[parentId] = {
              width: childMaxX + ANCESTOR_BUFFER,
              height: childMaxY + ANCESTOR_BUFFER + HEADER,
            };
            currentId = parentId;
            currentW = ancestorResizes[parentId].width;
            currentH = ancestorResizes[parentId].height;
          }
        }

        expandedNodesRef.current.set(nodeId, {
          originalNode: { ...originalNode },
          childNodeIds,
          childEdgeIds,
          ancestorOriginalStyles,
        });

        setInstance((prev) => {
          const sized = prev.child_nodes.map((n) => {
            if (n.id === nodeId) {
              return {
                ...n,
                type: 'subflowGroup',
                draggable: false,
                zIndex: 0,
                style: { width: groupWidth, height: groupHeight },
                data: {
                  ...n.data,
                  isExpanded: true,
                  label: n.data?.label || originalNode?.name,
                },
              };
            }
            if (ancestorResizes[n.id]) {
              return {
                ...n,
                style: {
                  ...n.style,
                  width: ancestorResizes[n.id].width,
                  height: ancestorResizes[n.id].height,
                },
              };
            }
            return n;
          });

          const childIds = new Set(childrenWithPositions.map((n) => n.id));
          const dedupedSized = sized.filter((n) => !(n.hidden && childIds.has(n.id)));
          const withChildren = [...dedupedSized, ...childrenWithPositions];
          const allEdges = [...prev.child_edges, ...mappedEdges];
          const changedGroups = new Set([nodeId, ...Object.keys(ancestorResizes)]);
          const groupChildPositions: Record<string, { x: number; y: number }> = {};
          const groupSizeOverrides: Record<string, { width: number; height: number }> = {};
          changedGroups.forEach((gid) => {
            const layout = getGroupChildLayout(withChildren, allEdges, gid);
            Object.assign(groupChildPositions, layout.positions);
            if (layout.width > 0)
              groupSizeOverrides[gid] = {
                width: layout.width,
                height: layout.height,
              };
          });
          const resizedNodes = withChildren.map((n) => {
            if (groupSizeOverrides[n.id])
              return {
                ...n,
                style: { ...n.style, ...groupSizeOverrides[n.id] },
              };
            return n;
          });
          const dagrePositions = getDagrePositions(resizedNodes, allEdges);
          const newNodes = resizedNodes.map((n) => {
            if (groupChildPositions[n.id]) return { ...n, position: groupChildPositions[n.id] };
            if (dagrePositions[n.id]) return { ...n, position: dagrePositions[n.id] };
            return n;
          });

          return {
            ...prev,
            child_nodes: newNodes,
            child_edges: allEdges,
          };
        });

        setTimeout(() => fitView({ padding: 0.2 }), 200);
      } catch (error) {
        console.error('[InstanceViewer] Failed to expand subflow:', error);
      }
    },
    [dispatch, fitView]
  );

  const isFlowNodeType = instance.type === 'flow';
  const canOpenUnblockDialog = canOpenUnblockNodeInstanceDialog(instance);
  const activeNodeInstanceId = instance.node_instance_id ?? nodeInstanceId;

  return (
    <Container fluid>
      <Row className="pt-4 pb-4 justify-content-lg-center sticky-top">
        <Col lg="12" className="d-flex align-items-center gap-3 flex-wrap">
          {' '}
          <h2 className="mb-0">
            Viewing instance {nodeInstanceId} -{' '}
            <NodeEditorLink nodeDefinitionId={nodeDefinitionId} nodeName={nodeEditorName} iconClassName="text-1100">
              {displayName}
            </NodeEditorLink>
          </h2>
          {canOpenUnblockDialog && (
            <Button variant="outline-primary" size="sm" onClick={() => setShowUnblockDialog(true)}>
              Unblock node instance...
            </Button>
          )}
          {navSlot}
        </Col>
      </Row>
      <Row className="mb-4 justify-content-lg-center">
        <Col lg="12">
          <Tabs id="bash-tabs" className="mb-2 sticky-tabs border-bottom" variant="underline" activeKey={activeTab} onSelect={handleTabSelect}>
            <Tab eventKey="general" title="General">
              <ReadOnlyProperties state={instance} />
            </Tab>
            <Tab eventKey="notes" title="Notes" mountOnEnter unmountOnExit>
              <NodeInstanceNotes nodeInstanceId={instance.node_instance_id} />
            </Tab>
            <Tab eventKey="version" title="Version" mountOnEnter unmountOnExit>
              <DefinitionVersion state={instance} />
            </Tab>
            {isFlowNodeType && (
              <Tab eventKey="flow" title="Flow">
                <Flow
                  disabled
                  state={instance}
                  setState={setInstance}
                  onChange={() => {}}
                  onNodeDoubleClickHandler={onNodeDoubleClickHandler}
                  showNodeLibrary={false}
                  onFetchSubflow={handleFetchSubflow}
                  onNavChange={setNavSlot}
                  onExpandNode={handleExpandNode}
                />
              </Tab>
            )}
            <Tab eventKey="waitEvents" title="Wait Events" mountOnEnter unmountOnExit>
              <WaitEvents nodeInstanceId={instance.node_instance_id} />
            </Tab>
            <Tab eventKey="statusHistory" title="Status History">
              <StatusHistory
                entries={instance.status_updates || []}
                instanceTimezone={instance.scheduled_start_timezone}
                loading={instanceLoading}
                error={instanceError}
              />
            </Tab>
          </Tabs>
        </Col>
      </Row>
      <UnblockNodeInstanceDialog
        show={showUnblockDialog}
        nodeInstanceId={activeNodeInstanceId}
        onHide={() => setShowUnblockDialog(false)}
        onChanged={() => setInstanceRefreshToken((current) => current + 1)}
      />
    </Container>
  );
};

export default InstanceViewer;
